import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import useStore from "../../store/useStore";

import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

const NAV_ITEMS = [
  { label: "My Account", path: "profile", icon: PersonOutlinedIcon },
  { label: "My Orders", path: "orders", icon: ShoppingBagOutlinedIcon },
  { label: "Track Order", path: "track", icon: LocalShippingOutlinedIcon },
  { label: "My List", path: "wishlist", icon: FavoriteBorderIcon },
  { label: "Wallet", path: "wallet", icon: AccountBalanceWalletOutlinedIcon },
  { label: "Subscriptions", path: "subscriptions", icon: AutorenewOutlinedIcon },
  { label: "Referral", path: "referral", icon: CardGiftcardOutlinedIcon },
  { label: "My Address", path: "addresses", icon: LocationOnOutlinedIcon },
  {
    label: "Notifications",
    path: "notifications",
    icon: NotificationsNoneOutlinedIcon,
  },
  { label: "Help & Support", path: "help", icon: HelpOutlineOutlinedIcon },
];

const AccountPage = () => {
  const { isAuthenticated, user, logout, openAuthModal, wishlist_count } =
    useStore();
  const navigate = useNavigate();

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-16">
        <div className="mx-auto max-w-sm rounded-lg border border-gray-200 bg-white p-6 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 text-gray-600">
            <LockOutlinedIcon style={{ fontSize: 28 }} />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">
            Access Your Account
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            Sign in to manage your profile, orders, subscriptions, and saved
            details.
          </p>
          <button
            onClick={openAuthModal}
            className="mt-6 w-full rounded-md bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700"
          >
            Login / Sign Up
          </button>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const displayName =
    user?.name ||
    user?.full_name ||
    user?.customer?.name ||
    user?.mobile ||
    user?.customer?.mobile ||
    "User";
  const mobile = user?.mobile || user?.customer?.mobile || null;
  const initials = displayName.charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4 lg:hidden">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-sm font-semibold text-primary-700">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-gray-900">
                {displayName}
              </p>
              {mobile && (
                <p className="truncate text-xs text-gray-500">+91 {mobile}</p>
              )}
            </div>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-lg border border-gray-200 bg-white">
              <div className="border-b border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-50 text-sm font-semibold text-primary-700">
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-900">
                      {displayName}
                    </p>
                    {mobile && (
                      <p className="truncate text-xs text-gray-500">
                        +91 {mobile}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <nav className="p-2">
                {NAV_ITEMS.map(({ label, path, icon: Icon }) => (
                  <NavLink
                    key={path}
                    to={`/account/${path}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium ${
                        isActive
                          ? "bg-primary-50 text-primary-700"
                          : "text-gray-700 hover:bg-gray-50"
                      }`
                    }
                  >
                    <Icon style={{ fontSize: 18 }} />
                    <span className="min-w-0 flex-1 truncate">{label}</span>
                    {path === "wishlist" && wishlist_count > 0 && (
                      <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-600">
                        {wishlist_count}
                      </span>
                    )}
                  </NavLink>
                ))}
                <div className="my-2 border-t border-gray-200" />
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50"
                >
                  <LogoutOutlinedIcon style={{ fontSize: 18 }} />
                  <span>Logout</span>
                </button>
              </nav>
            </div>
          </aside>

          <main className="min-w-0">
            <div
              className="mb-4 flex gap-2 overflow-x-auto border-b border-gray-200 pb-3 lg:hidden"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {NAV_ITEMS.map(({ label, path, icon: Icon }) => (
                <NavLink
                  key={path}
                  to={`/account/${path}`}
                  className={({ isActive }) =>
                    `flex shrink-0 items-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold ${
                      isActive
                        ? "border-primary-600 bg-primary-50 text-primary-700"
                        : "border-gray-200 bg-white text-gray-700"
                    }`
                  }
                >
                  <Icon style={{ fontSize: 16 }} />
                  <span>{label}</span>
                  {path === "wishlist" && wishlist_count > 0 && (
                    <span className="rounded-full bg-red-100 px-1.5 text-[10px] text-red-600">
                      {wishlist_count}
                    </span>
                  )}
                </NavLink>
              ))}
              <button
                onClick={handleLogout}
                className="flex shrink-0 items-center gap-2 rounded-md border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-600"
              >
                <LogoutOutlinedIcon style={{ fontSize: 16 }} />
                Logout
              </button>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
